// Showcase lineup structure + corps metadata.
//
// 2026 had a fixed corps repertoire. Later years will not re-use those
// pieces — defaults below only apply to 2026. Other years start with solo
// blocks only (no corps slots) unless a structure is stored on
// iabf-config-{year}/showcase-runtime.showcaseStructure.

import { IABF_DANCER_YEAR } from '@/config/iabf-event'

/** 2026-only corps participation fields + choreographers. */
export const CORPS_INFO_2026 = {
  'Alice in Wonderland': { field: 'aliceInWonderland', choreographer: 'Kelsey Maloney-Steiner' },
  'Becoming': { field: 'becoming', choreographer: 'Cindy Heen' },
  'Waltz of the Hours': { field: 'waltzOfHours', choreographer: 'Julie Leung' },
  'Le Jazz Hot': { field: 'leJazzHot', choreographer: 'Sean Kelly' },
  'Garland Waltz': { field: 'garlandWaltz', choreographer: 'Jena Kocsis' }
}

/** Role display order for 2026 corps casts. */
export const ROLE_ORDER_2026 = {
  'Alice in Wonderland': ['Pointe', 'Flat Ladies', 'Queen of Hearts', 'Men & Jokers'],
  'Garland Waltz': ['Leaders', 'Pointe', 'Intermediate', 'Flat'],
  'Becoming': ['Small Ensemble', 'Full Group']
}

/**
 * Full 2026 show order (corps + solo blocks + intermission).
 * `half` is used by admin runtime estimates.
 */
export const SHOWCASE_STRUCTURE_2026 = [
  { type: 'soloBlock', block: 'AA', name: 'Solo Block AA', subtitle: 'Opening Remarks + 1 solo', half: 1 },
  { type: 'corps', name: 'Alice in Wonderland', half: 1 },
  { type: 'soloBlock', block: 'A', name: 'Solo Block A', half: 1 },
  { type: 'corps', name: 'Becoming', half: 1 },
  { type: 'soloBlock', block: 'B', name: 'Solo Block B', half: 1 },
  { type: 'intermission', name: '— Intermission —' },
  { type: 'corps', name: 'Waltz of the Hours', half: 2 },
  { type: 'soloBlock', block: 'C', name: 'Solo Block C', half: 2 },
  { type: 'corps', name: 'Le Jazz Hot', half: 2 },
  { type: 'soloBlock', block: 'D', name: 'Solo Block D', half: 2 },
  { type: 'corps', name: 'Garland Waltz', half: 2 }
]

/**
 * Year-agnostic skeleton: solo blocks + intermission only.
 * Corps pieces for 2027+ will be added via config or a later admin UI.
 */
export const SHOWCASE_STRUCTURE_GENERIC = [
  { type: 'soloBlock', block: 'AA', name: 'Solo Block AA', subtitle: 'Opening Remarks + 1 solo', half: 1 },
  { type: 'soloBlock', block: 'A', name: 'Solo Block A', half: 1 },
  { type: 'soloBlock', block: 'B', name: 'Solo Block B', half: 1 },
  { type: 'intermission', name: '— Intermission —' },
  { type: 'soloBlock', block: 'C', name: 'Solo Block C', half: 2 },
  { type: 'soloBlock', block: 'D', name: 'Solo Block D', half: 2 }
]

/**
 * Default structure for a festival year (no Firestore override).
 * Only 2026 includes the five corps pieces.
 */
export function defaultShowcaseStructure (year = IABF_DANCER_YEAR) {
  if (Number(year) === 2026) {
    return SHOWCASE_STRUCTURE_2026.map(s => ({ ...s }))
  }
  return SHOWCASE_STRUCTURE_GENERIC.map(s => ({ ...s }))
}

export function defaultCorpsInfo (year = IABF_DANCER_YEAR) {
  if (Number(year) === 2026) {
    return { ...CORPS_INFO_2026 }
  }
  return {}
}

export function defaultRoleOrder (year = IABF_DANCER_YEAR) {
  if (Number(year) === 2026) {
    return { ...ROLE_ORDER_2026 }
  }
  return {}
}

/**
 * Resolve structure: prefer saved config.showcaseStructure, else year default.
 * @param {number} year
 * @param {object|null} configDoc - showcase-runtime doc data
 */
export function resolveShowcaseStructure (year, configDoc) {
  const saved = configDoc && Array.isArray(configDoc.showcaseStructure) && configDoc.showcaseStructure.length
    ? configDoc.showcaseStructure
    : null
  if (saved) {
    return saved.map(s => ({ ...s }))
  }
  return defaultShowcaseStructure(year)
}

export function resolveCorpsInfo (year, configDoc) {
  const saved = configDoc && configDoc.corpsInfo && typeof configDoc.corpsInfo === 'object'
    ? configDoc.corpsInfo
    : null
  if (saved && Object.keys(saved).length) {
    return { ...saved }
  }
  return defaultCorpsInfo(year)
}
